/**
 * featureFlags.js - Cliente de LaunchDarkly y estado reactivo de los flags
 *
 * Es un módulo JS plano (como versionState.js) para que el GuardRouter, que no
 * puede usar composables, consulte los flags. Los componentes lo leen con
 * useFeatureFlag().
 *
 * El cliente se crea una sola vez, con un contexto anónimo, y mantiene una
 * conexión de streaming: un cambio en el dashboard de LaunchDarkly llega a la
 * app abierta sin recargar.
 */

/* global __APP_VERSION__ */
import { reactive, readonly } from 'vue';
import { createClient } from '@launchdarkly/js-client-sdk';
import * as Sentry from '@sentry/vue';
import { FEATURE_FLAGS, FEATURE_FLAGS_CONFIG } from '@/config/featureFlags';

const flagDefinitions = Object.values(FEATURE_FLAGS);

const state = reactive({
  ready: false,
  flags: Object.fromEntries(flagDefinitions.map((flag) => [flag.key, flag.default])),
});

let client = null;
let initPromise = null;

const syncFlags = () => {
  flagDefinitions.forEach(({ key, default: fallback }) => {
    state.flags[key] = client.variation(key, fallback);
  });
};

const startClient = async () => {
  const clientSideId = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;
  if (!clientSideId) {
    console.warn('[featureFlags] VITE_LAUNCHDARKLY_CLIENT_ID no definido, se usan los defaults');
    return;
  }

  try {
    client = createClient(clientSideId, { kind: 'user', anonymous: true }, {
      streaming: true,
      fetchGoals: false,
      applicationInfo: {
        id: FEATURE_FLAGS_CONFIG.APPLICATION_ID,
        version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
      },
    });

    flagDefinitions.forEach(({ key }) => client.on(`change:${key}`, syncFlags));

    const result = await client.start({ timeout: FEATURE_FLAGS_CONFIG.INIT_TIMEOUT_SECONDS });

    // Con timeout o error el SDK sigue reintentando en segundo plano: cuando llegan
    // los flags, los eventos change:* actualizan el estado
    if (result.status !== 'complete') {
      console.warn(`[featureFlags] LaunchDarkly no inicializó (${result.status})`, result.error);
      Sentry.addBreadcrumb({
        category: 'feature_flags',
        level: 'warning',
        message: `LaunchDarkly init ${result.status}`,
        data: { error: result.error?.message },
      });
    }

    syncFlags();
  } catch (error) {
    console.error('[featureFlags] Error creando el cliente de LaunchDarkly', error);
    Sentry.captureException(error, { tags: { feature: 'feature_flags' } });
  }
};

/**
 * Inicia el cliente de LaunchDarkly. Es idempotente y la promesa nunca se rechaza:
 * si LaunchDarkly falla, los flags quedan con su valor en caché o su default.
 * @returns {Promise<void>}
 */
export const initFeatureFlags = () => {
  if (!initPromise) {
    initPromise = startClient().finally(() => {
      state.ready = true;
    });
  }
  return initPromise;
};

/**
 * Valor actual de un flag (definido en config/featureFlags.js)
 * @param {string} key
 * @returns {*}
 */
export const getFlag = (key) => state.flags[key];

export const featureFlagsState = readonly(state);
